import { isArray } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";

// Imported actions
import { searchChargingStationsFull } from "../../../../actions/search.js";

// Imported components
import ClusteredMarkers from "./ClusteredMarkers.js";
import FilterControls from "./FilterControls.tsx";
import InfoView from "./InfoView.tsx";
import MapStyleControls from "./MapStyleControls.tsx";

// Imported contexts
import { useUserContext } from "../../../../contexts/User.tsx";

// Imported utils
import { deepCopy } from "../../../../utils/misc.js";
import { isLoggedIn } from "../../../../utils/user.js";

const mapStyle = {
  height: "100%",
  width: "100%",
};

const defaultCenter = {
  lat: 39.8283, // Center of the continental US
  lng: -98.5795,
};

const mapOptions = {
  // colorScheme: "DARK",
  clickableIcons: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: true,
  minZoom: 4,
  maxZoom: 20,
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "landscape",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const Map = ({ accessTypes, locationStatusTypes, locationTypes }) => {
  const map = useMap();
  const appLocation = useLocation();
  const navigate = useNavigate();

  const { user } = useUserContext();

  // Markers contains all markers/stations that the current operator has access to
  const [markers, setMarkers] = useState({
    type: "FeatureCollection",
    features: {},
  });

  // map style == mapTypeId
  const [mapTypeId, setMapTypeId] = useState("roadmap");

  // Selected station is the marker/station that is currently selected on the map,
  // with info displayed in the sideview
  const [selectedStation, setSelectedStation] = useState(null);

  const [filters, setFilters] = useState({
    accessTypes: accessTypes.map((type) => type.codeName),
    locationStatusTypes: locationStatusTypes.map((type) => type.codeName),
    locationTypes: locationTypes.map((type) => type.codeName),
  });

  // TODO: remove temp userId checks below
  const [mapCenter, setMapCenter] = useState(
    user.userId === "tony.k.walters@cnmp.com"
      ? { lat: 37.1661, lng: -119.4494 }
      : defaultCenter
  );
  // TODO: remove temp userId checks below
  const [mapZoom, setMapZoom] = useState(
    user.userId === "tony.k.walters@cnmp.com" ? 6.6 : mapOptions.minZoom
  );

  const onStationClose = () => {
    setSelectedStation(null);
  };

  const handleMarkerClick = useCallback(
    (data) => {
      console.log(data);
      console.log(!!map);
      console.log(isArray(data));
      if (map && isArray(data)) {
        setMapCenter(null);
        setMapZoom(null);
        let bounds = new window.google.maps.LatLngBounds();
        Object.values(data)
          .map((marker) => ({
            lat: Number(marker.lat),
            lng: Number(marker.lon),
          }))
          .forEach((marker) => bounds.extend(marker));
        map.fitBounds(bounds, 20);
      } else {
        setMapCenter({
          lat: Number(data.lat),
          lng: Number(data.lon),
        });
        setSelectedStation(data);
      }
    },
    [map, setMapCenter, setMapZoom, setSelectedStation]
  );

  // Grabs station data from the API on load and interval
  useEffect(() => {
    const searchStationsFull = async () => {
      const stations = await searchChargingStationsFull(false, 0, 99999)
        .then(async (stations) => {
          // console.log(stations);
          let markersData = {
            type: "FeatureCollection",
            features: {},
          };

          markersData.features = stations.map((station) => {
            return {
              type: "Feature",
              id: station.id,
              geometry: {
                type: "Point",
                coordinates: [Number(station.lon), Number(station.lat)],
              },
              properties: {
                data: station,
              },
            };
          });

          setMarkers(markersData);
          return stations;
        })
        .catch((err) => {
          // handle error
        });
      return stations;
    };

    const attemptFetch = () => {
      if (!isLoggedIn()) {
        navigate("/login", {
          replace: true,
          state: { path: appLocation.pathname },
        });
      } else {
        searchStationsFull().then((stations) => {
          if (appLocation.state) {
            let stationId = appLocation.state.station;
            let equipmentId = appLocation.state.equipment;

            let stationData = stations.find(
              (station) => station.id === stationId
            );

            setMapCenter({
              lat: Number(stationData.lat),
              lng: Number(stationData.lon),
            });
            setMapZoom(14);
            setSelectedStation(stationData);

            appLocation.state = null;
          }
        });
      }
    };

    attemptFetch();

    const interval = setInterval(attemptFetch, 10000);

    return () => clearInterval(interval);
  }, []);

  // helper function passed into the Filter Controls component
  const handleFilterChange = (filters) => {
    setSelectedStation(null);
    setFilters(filters);
  };

  const filteredMarkers = useMemo(() => {
    if (!markers) return null;

    let filteredMarkers = deepCopy(markers);

    // Filter the markers based on selected filters
    let features = markers.features;
    if (isArray(features)) {
      features = features.filter((feature) => {
        let accTypeMatch = false,
          locStatusTypeMatch = false,
          locTypeMatch = false;

        for (let i = 0; i < filters.locationTypes.length; i++) {
          locTypeMatch =
            locTypeMatch ||
            feature.properties.data.locationType.codeName ===
              filters.locationTypes[i];
          if (locTypeMatch) break;
        }

        for (let i = 0; i < filters.accessTypes.length; i++) {
          accTypeMatch =
            accTypeMatch ||
            feature.properties.data.accessType.codeName ===
              filters.accessTypes[i];
          if (accTypeMatch) break;
        }

        for (let i = 0; i < filters.locationStatusTypes.length; i++) {
          locStatusTypeMatch =
            locStatusTypeMatch ||
            feature.properties.data.locationStatusType.codeName ===
              filters.locationStatusTypes[i];
          if (locStatusTypeMatch) break;
        }

        return accTypeMatch && locStatusTypeMatch && locTypeMatch;
      });

      filteredMarkers.features = features;
    }

    return filteredMarkers;
  }, [markers, filters]);

  const handleCameraChange = useCallback((e) => {
    setMapCenter(e.detail.center);
    setMapZoom(e.detail.zoom);
  });

  // Helper function passed into the MapStyleControls component
  // the function is called by the lower component
  const handleMapTypeChange = (type) => {
    setMapTypeId(type);
  };

  return (
    <>
      <MapStyleControls
        defaultStyle={mapTypeId}
        handleMapTypeChange={handleMapTypeChange}
      />

      <FilterControls
        accessTypes={accessTypes.map((t) => t.codeName)}
        handleFilterChange={handleFilterChange}
        locationStatusTypes={locationStatusTypes.map((t) => t.codeName)}
        locationTypes={locationTypes.map((t) => t.codeName)}
      />

      <GoogleMap
        {...mapOptions}
        center={mapCenter}
        className={"map-view-map"}
        disableDefaultUI={true}
        gestureHandling={"greedy"}
        mapId={"704f78d978e3652a"}
        mapTypeId={mapTypeId}
        onCameraChanged={handleCameraChange}
        onClick={() => setSelectedStation(null)}
        reuseMaps={false}
        style={mapStyle}
        zoom={mapZoom}
      >
        {filteredMarkers && (
          <ClusteredMarkers
            filteredMarkers={filteredMarkers}
            maxZoom={mapOptions.maxZoom - 1}
            onClick={handleMarkerClick}
            selectedStation={selectedStation}
            setSelectedStation={setSelectedStation}
          />
        )}
      </GoogleMap>

      {selectedStation && (
        <InfoView
          onClose={() => onStationClose()}
          stationData={selectedStation}
        />
      )}
    </>
  );
};

export default Map;
